import React, { useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { RENT_CHARGE_FIELDS, getInvoiceChargeValue } from "./invoiceDraft";
import "./OriginalInvoiceModal.scss";

const comparisonFields = [
  ...RENT_CHARGE_FIELDS,
  { key: "exi", label: "Extra Charges" },
  { key: "mgmt_fee", label: "Management Fee" },
];

function formatChange(current, original) {
  if (original === null) return "Unavailable";
  const difference = Number((Number(current) - Number(original)).toFixed(2));
  if (difference === 0) return "—";
  return difference > 0 ? `+${difference}` : String(difference);
}

export default function OriginalInvoiceModal({ invoice, onClose }) {
  const dialogRef = useRef(null);
  const titleRef = useRef(null);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    dialog.showModal();
    titleRef.current?.focus();
    document.body.style.overflow = "hidden";

    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  const handleBackdropClick = (event) => {
    if (event.target !== event.currentTarget) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    if (
      event.clientX < bounds.left || event.clientX > bounds.right ||
      event.clientY < bounds.top || event.clientY > bounds.bottom
    ) onClose();
  };

  const rows = comparisonFields.map(({ key, label }) => {
    const current = Number(getInvoiceChargeValue(invoice, key));
    const original = invoice.originalCharges?.[key] ??
      (invoice.isEdited?.[key] === true ? null : current);
    return { key, label, current, original, changed: original === null ?
      invoice.isEdited?.[key] === true : Number(original) !== current };
  });
  const hasMissingOriginals = rows.some((row) => row.original === null);
  const originalTotal = hasMissingOriginals ? null : rows.reduce((sum, row) => sum + Number(row.original), 0);
  const currentTotal = invoice.total ?? invoice.amount ?? rows.reduce((sum, row) => sum + row.current, 0);
  const houseNumber = invoice.tenant?.house?.house_no ?? invoice.house_no;

  return createPortal(
    <dialog
      ref={dialogRef}
      aria-labelledby="original-invoice-title"
      className="original-invoice-dialog"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={handleBackdropClick}
    >
      <h2 ref={titleRef} tabIndex={-1} id="original-invoice-title" className="original-invoice-title">
        Original invoice{houseNumber ? ` — House ${houseNumber}` : ""}
      </h2>
      <div className="original-invoice-content">
        <p>Compare the invoice before manual edits with its current values. Changed charges are highlighted.</p>
        {hasMissingOriginals && <p className="missing-originals">Some original values were not recorded for this older invoice.</p>}
        <table className="invoice-comparison-table">
          <thead><tr><th scope="col">Charge</th><th scope="col">Original</th><th scope="col">Current</th><th scope="col">Change</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className={row.changed ? "changed-charge" : undefined}>
                <th scope="row">{row.label}</th>
                <td>{row.original ?? "Unavailable"}</td>
                <td>{row.current}</td>
                <td>{formatChange(row.current, row.original)}</td>
              </tr>
            ))}
            <tr className="comparison-total"><th scope="row">Total</th><td>{originalTotal ?? "Unavailable"}</td><td>{currentTotal}</td><td>{formatChange(currentTotal, originalTotal)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="original-invoice-actions">
        <button type="button" className="original-invoice-close-button" onClick={onClose}>Close</button>
      </div>
    </dialog>,
    document.body
  );
}
